import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError, type CommonCode } from '../lib/api'
import {
  adminApi,
  can,
  type AdminOrg,
  type AdminProblemCard,
  type AdminSolutionCard,
  type DatasetFilters,
  type DatasetKey,
  type ExportedFile,
} from '../lib/adminApi'
import {
  URGENCY_LABEL,
  makeLabeler,
  makeRegionLabeler,
  useCommonCodes,
  useDongs,
  useRegions,
  useTargetCodes,
} from '../lib/codes'

/**
 * 데이터 관리 — 과업지시서 §3-나-5(검색·필터·엑셀/CSV 다운로드), §8 검수 11항.
 *
 * 검수 화면과 나눠 둔 이유가 있다. 저기는 **한 건씩 판단**하는 곳이고 여기는
 * **덩어리로 뽑아 밖에서 쓰는** 곳이다. 그래서 여기에는 상태 전환 버튼이 없다 —
 * 목록을 훑다 실수로 공개를 누르는 일을 구조로 막는다.
 *
 * **화면 건수와 파일 건수는 같다.** 목록과 내려받기가 같은 조건을 같은 서버 빌더로 보낸다.
 * 다만 화면은 페이지 단위로 끊어 보여주고, 파일은 조건에 걸린 전부를 담는다.
 */

const PAGE_SIZE = 50

const TABS: { key: DatasetKey; label: string }[] = [
  { key: 'solution-cards', label: '해결카드' },
  { key: 'problem-cards', label: '시민문제카드' },
  { key: 'orgs', label: '기업' },
]

/** 필터 select용 사본. 행에 찍는 라벨은 서버가 준 `reviewStatusLabel`을 쓴다. */
const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'draft', label: '작성중' },
  { value: 'review_requested', label: '검토요청' },
  { value: 'published', label: '공개' },
  { value: 'revision_needed', label: '수정필요' },
  { value: 'suspended', label: '중단' },
  { value: 'expired', label: '만료' },
]

const PUBLISHED_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'true', label: '공개' },
  { value: 'false', label: '비공개' },
]

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return error.detail[0] ?? '이 작업을 할 권한이 없습니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

/**
 * 받은 파일을 디스크에 내린다. `<a download>`을 쓰는 이유는 주소로 바로 여는 방식과 달리
 * **실패를 오류로 받아 화면에 띄울 수 있기** 때문이다(주소로 열면 403 JSON이 탭에 그대로 뜬다).
 */
function saveFile(file: ExportedFile): void {
  const url = URL.createObjectURL(file.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  // 즉시 회수하면 일부 브라우저가 저장을 시작하기 전에 URL이 사라진다.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function AdminDataPage() {
  const me = useAdminMe()
  const [tab, setTab] = useState<DatasetKey>('solution-cards')
  const [filters, setFilters] = useState<DatasetFilters>({})
  const [offset, setOffset] = useState(0)
  const [includeContact, setIncludeContact] = useState(false)

  const mayExportPii = can(me.data, 'export', 'all')
  const mayExport = mayExportPii || can(me.data, 'export', 'no_pii')

  const download = useMutation({
    mutationFn: (format: 'csv' | 'xlsx') =>
      adminApi.exportDataset(tab, filters, format, tab === 'orgs' && includeContact),
    onSuccess: saveFile,
  })

  /** 탭을 옮겨도 검색어·기간은 남긴다 — 같은 기간을 세 번 다시 입력하게 하지 않는다. */
  const switchTab = (next: DatasetKey): void => {
    setTab(next)
    // 값이 없는 키는 아예 넣지 않는다 — `q: undefined`를 담으면 "빈 검색어"와 구분이 사라진다.
    setFilters((f) => {
      const kept: DatasetFilters = {}
      if (f.q !== undefined) kept.q = f.q
      if (f.from !== undefined) kept.from = f.from
      if (f.to !== undefined) kept.to = f.to
      return kept
    })
    setOffset(0)
    setIncludeContact(false)
    download.reset()
  }

  const patch = (v: Partial<DatasetFilters>): void => {
    setFilters((f) => ({ ...f, ...v }))
    setOffset(0)
    download.reset()
  }

  const query: DatasetFilters = { ...filters, limit: PAGE_SIZE, offset }
  const downloadError = errorMessage(download.error)

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">데이터 내보내기</h1>
          <p className="page-desc">
            조건으로 걸러 보고, 그 조건 그대로 CSV·엑셀로 내려받습니다. 화면에 보이는 목록과
            받는 파일은 <b>같은 조건</b>으로 만들어집니다.
          </p>
        </div>
      </div>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={t.key === tab ? 'active' : undefined}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <FilterBar dataset={tab} filters={filters} onChange={patch} />

      <div className="export-bar">
        {mayExport ? (
          <>
            <button
              type="button"
              className="btn"
              disabled={download.isPending}
              onClick={() => download.mutate('csv')}
            >
              CSV 내려받기
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={download.isPending}
              onClick={() => download.mutate('xlsx')}
            >
              엑셀(XLSX) 내려받기
            </button>
            {tab === 'orgs' && mayExportPii && (
              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={includeContact}
                  onChange={(e) => setIncludeContact(e.target.checked)}
                />
                담당자 연락처 포함
              </label>
            )}
            <span className="muted small">
              내려받기는 누가·무엇을·몇 건 받았는지 기록에 남습니다.
              {tab === 'orgs' && !mayExportPii && ' 담당자 연락처는 이 계정으로 받을 수 없습니다.'}
            </span>
          </>
        ) : (
          <span className="muted small">
            이 계정에는 내려받기 권한이 없습니다(<b>export</b> 권한 필요).
          </span>
        )}
      </div>

      {downloadError !== null && (
        <div className="notice error" role="alert">
          {downloadError}
        </div>
      )}

      {tab === 'solution-cards' && (
        <SolutionTable query={query} offset={offset} onOffset={setOffset} />
      )}
      {tab === 'problem-cards' && (
        <ProblemTable query={query} offset={offset} onOffset={setOffset} />
      )}
      {tab === 'orgs' && <OrgTable query={query} offset={offset} onOffset={setOffset} />}
    </div>
  )
}

/* ── 필터 ─────────────────────────────────────────────────── */

function CodeSelect(props: {
  id: string
  label: string
  value: string | undefined
  codes: CommonCode[] | undefined
  onChange: (v: string) => void
}) {
  return (
    <div className="filter-field">
      <label htmlFor={props.id} className="muted small">
        {props.label}
      </label>
      <select id={props.id} value={props.value ?? ''} onChange={(e) => props.onChange(e.target.value)}>
        <option value="">전체</option>
        {(props.codes ?? []).map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function PlainSelect(props: {
  id: string
  label: string
  value: string | undefined
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="filter-field">
      <label htmlFor={props.id} className="muted small">
        {props.label}
      </label>
      <select id={props.id} value={props.value ?? ''} onChange={(e) => props.onChange(e.target.value)}>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function FilterBar(props: {
  dataset: DatasetKey
  filters: DatasetFilters
  onChange: (v: Partial<DatasetFilters>) => void
}) {
  const { dataset, filters, onChange } = props
  const categories = useCommonCodes('category')
  const targets = useTargetCodes()
  const resources = useCommonCodes('resource')
  const serviceAreas = useCommonCodes('service_area')
  const orgTypes = useCommonCodes('org_type')
  const operationStatuses = useCommonCodes('operation_status')
  const urgencies = useCommonCodes('urgency')
  const dongs = useDongs()

  const dongCodes: CommonCode[] = (dongs.data ?? []).map((r) => ({
    code: r.code,
    // 지역은 `common_code`가 아니라 `region` 테이블에서 온다. 필터 컴포넌트가 받는 타입을
    // 맞추려고 붙이는 값이라 소비처가 없다 — 레벨 값(`emd`)과 헷갈리지 않게 출처를 적는다.
    group: 'region',
    label: r.name,
    parentCode: r.parentCode,
    sortOrder: 0,
    isActive: true,
    note: null,
  }))

  return (
    <div className="filters data-filters">
      <div className="filter-field grow">
        <label htmlFor="data-q" className="muted small">
          검색어
        </label>
        <input
          id="data-q"
          type="search"
          value={filters.q ?? ''}
          placeholder={dataset === 'orgs' ? '기업명·소개' : '제목·요약'}
          onChange={(e) => onChange({ q: e.target.value })}
        />
      </div>

      <div className="filter-field">
        <label htmlFor="data-from" className="muted small">
          기간 시작
        </label>
        <input
          id="data-from"
          type="date"
          value={filters.from ?? ''}
          onChange={(e) => onChange({ from: e.target.value })}
        />
      </div>
      <div className="filter-field">
        <label htmlFor="data-to" className="muted small">
          기간 끝
        </label>
        <input
          id="data-to"
          type="date"
          value={filters.to ?? ''}
          onChange={(e) => onChange({ to: e.target.value })}
        />
      </div>

      {dataset !== 'orgs' && (
        <PlainSelect
          id="data-status"
          label="검수 상태"
          value={filters.status}
          options={STATUS_OPTIONS}
          onChange={(v) => onChange({ status: v })}
        />
      )}

      {dataset !== 'orgs' && (
        <>
          <CodeSelect
            id="data-category"
            label="분야"
            value={filters.categoryCodes}
            codes={categories.data}
            onChange={(v) => onChange({ categoryCodes: v })}
          />
          <CodeSelect
            id="data-target"
            label="대상"
            value={filters.targetCodes}
            codes={targets.data}
            onChange={(v) => onChange({ targetCodes: v })}
          />
        </>
      )}

      {dataset === 'solution-cards' && (
        <>
          <CodeSelect
            id="data-help"
            label="제공 자원"
            value={filters.resourceCodes}
            codes={resources.data}
            onChange={(v) => onChange({ resourceCodes: v })}
          />
          <CodeSelect
            id="data-area"
            label="서비스 지역"
            value={filters.serviceAreaCodes}
            codes={serviceAreas.data}
            onChange={(v) => onChange({ serviceAreaCodes: v })}
          />
        </>
      )}

      {dataset === 'problem-cards' && (
        <>
          <CodeSelect
            id="data-region"
            label="지역"
            value={filters.regionCode}
            codes={dongCodes}
            onChange={(v) => onChange({ regionCode: v })}
          />
          <CodeSelect
            id="data-urgency"
            label="시급성"
            value={filters.urgency}
            codes={urgencies.data}
            onChange={(v) => onChange({ urgency: v })}
          />
        </>
      )}

      {dataset === 'orgs' && (
        <>
          <CodeSelect
            id="data-orgtype"
            label="기업 유형"
            value={filters.orgType}
            codes={orgTypes.data}
            onChange={(v) => onChange({ orgType: v })}
          />
          <CodeSelect
            id="data-base-region"
            label="거점 동"
            value={filters.baseRegionCode}
            codes={dongCodes}
            onChange={(v) => onChange({ baseRegionCode: v })}
          />
          <CodeSelect
            id="data-op-status"
            label="운영 상태"
            value={filters.operationStatus}
            codes={operationStatuses.data}
            onChange={(v) => onChange({ operationStatus: v })}
          />
        </>
      )}

      {dataset !== 'solution-cards' && (
        <PlainSelect
          id="data-published"
          label="공개 여부"
          value={filters.isPublished}
          options={PUBLISHED_OPTIONS}
          onChange={(v) => onChange({ isPublished: v })}
        />
      )}
    </div>
  )
}

/* ── 목록 ─────────────────────────────────────────────────── */

function Pager(props: {
  total: number
  offset: number
  count: number
  onOffset: (v: number) => void
}) {
  const { total, offset, count } = props
  const first = total === 0 ? 0 : offset + 1
  const last = offset + count
  return (
    <div className="pager">
      <span className="muted small">
        전체 <b>{total}</b>건 중 {first}–{last}
      </span>
      <div className="row-actions">
        <button
          type="button"
          className="btn ghost small-btn"
          disabled={offset === 0}
          onClick={() => props.onOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          이전
        </button>
        <button
          type="button"
          className="btn ghost small-btn"
          disabled={last >= total}
          onClick={() => props.onOffset(offset + PAGE_SIZE)}
        >
          다음
        </button>
      </div>
    </div>
  )
}

function SolutionTable(props: {
  query: DatasetFilters
  offset: number
  onOffset: (v: number) => void
}) {
  const q = useQuery({
    queryKey: ['admin-data', 'solution-cards', props.query],
    queryFn: () => adminApi.solutionCards(props.query),
  })
  const categories = useCommonCodes('category')
  const categoryLabel = makeLabeler(categories.data)
  const items: AdminSolutionCard[] = q.data?.items ?? []

  return (
    <QueryState isPending={q.isPending} error={q.error} isEmpty={items.length === 0}>
      <Pager
        total={q.data?.total ?? 0}
        offset={props.offset}
        count={items.length}
        onOffset={props.onOffset}
      />
      <table className="admin-table">
        <thead>
          <tr>
            <th>제목</th>
            <th>기업</th>
            <th>분야</th>
            <th>상태</th>
            <th>수정일</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td>
                <Link to={`/admin/cards/${c.id}`}>{c.title}</Link>
                <div className="muted small">{c.summary}</div>
              </td>
              <td>{c.org.name}</td>
              <td className="muted small">{c.categoryCodes.map(categoryLabel).join(', ')}</td>
              <td>{c.reviewStatusLabel}</td>
              <td className="muted small">{c.updatedAt.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  )
}

function ProblemTable(props: {
  query: DatasetFilters
  offset: number
  onOffset: (v: number) => void
}) {
  const q = useQuery({
    queryKey: ['admin-data', 'problem-cards', props.query],
    queryFn: () => adminApi.problemCards(props.query),
  })
  const items: AdminProblemCard[] = q.data?.items ?? []

  return (
    <QueryState isPending={q.isPending} error={q.error} isEmpty={items.length === 0}>
      <Pager
        total={q.data?.total ?? 0}
        offset={props.offset}
        count={items.length}
        onOffset={props.onOffset}
      />
      <table className="admin-table">
        <thead>
          <tr>
            <th>제목</th>
            <th>지역</th>
            <th>시급성</th>
            <th>상태</th>
            <th>공개</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td>
                <b>{c.title}</b>
              </td>
              <td>{c.region.name}</td>
              <td className="muted small">{URGENCY_LABEL[c.urgency] ?? c.urgency}</td>
              <td>{c.reviewStatusLabel}</td>
              <td>{c.isPublished ? '공개' : '비공개'}</td>
              <td className="muted small">{c.createdAt.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  )
}

function OrgTable(props: { query: DatasetFilters; offset: number; onOffset: (v: number) => void }) {
  const q = useQuery({
    queryKey: ['admin-data', 'orgs', props.query],
    queryFn: () => adminApi.orgs(props.query),
  })
  const orgTypes = useCommonCodes('org_type')
  const regions = useRegions()
  const orgTypeLabel = makeLabeler(orgTypes.data)
  const regionLabel = makeRegionLabeler(regions.data)
  const items: AdminOrg[] = q.data?.items ?? []

  return (
    <QueryState isPending={q.isPending} error={q.error} isEmpty={items.length === 0}>
      <Pager
        total={q.data?.total ?? 0}
        offset={props.offset}
        count={items.length}
        onOffset={props.onOffset}
      />
      <table className="admin-table">
        <thead>
          <tr>
            <th>기업명</th>
            <th>유형</th>
            <th>거점</th>
            <th>해결카드</th>
            <th>공개</th>
            <th>등록일</th>
          </tr>
        </thead>
        <tbody>
          {items.map((o) => (
            <tr key={o.id}>
              <td>
                <Link to={`/admin/orgs/${o.id}`}>{o.name}</Link>
              </td>
              <td className="muted small">{orgTypeLabel(o.orgType)}</td>
              <td className="muted small">{regionLabel(o.baseRegionCode)}</td>
              <td>{o.cardCount}건</td>
              <td>{o.isPublished ? '공개' : '비공개'}</td>
              <td className="muted small">{o.createdAt.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </QueryState>
  )
}
